import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";
import { Button } from "#components/atoms/Button/Button";
import { Divider } from "#components/atoms/Divider/Divider";
import { Hint } from "#components/atoms/Hint/Hint";
import { IconButton } from "#components/atoms/IconButton/IconButton";
import { ButtonSkeleton, Skeleton } from "#components/atoms/Skeleton/Skeleton";
import { Tab } from "#components/atoms/Tab/Tab";
import { Text } from "#components/atoms/Typographies/Text";
import { Title } from "#components/atoms/Typographies/Title";
import {
  SidePanel,
  SidePanelContent,
  SidePanelFooter,
  SidePanelHeader,
} from "#components/molecules/SidePanel/SidePanel";
import { useCompanyMachines } from "#modules/machines/view/useCompanyMachines";
import { formatGain } from "#modules/optimizations/domain/formatGain";
import { sumAppliedOptimizationsGain } from "#modules/optimizations/domain/getOptimizationGain";
import {
  matchesOptimizationFilter,
  type OptimizationFilter,
} from "#modules/optimizations/domain/optimizationFilters";
import { DiscardChangesModal } from "#modules/optimizations/view/DiscardChangesModal";
import { OptimizationCard } from "#modules/optimizations/view/OptimizationCard";
import { OptimizationDetail } from "#modules/optimizations/view/OptimizationDetail";
import { useIgnoredCompatibleLines } from "#modules/optimizations/view/useIgnoredCompatibleLines";
import { useOptimizations } from "#modules/optimizations/view/useOptimizations";
import { useToggleOptimization } from "#modules/optimizations/view/useToggleOptimization";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { computeQuoteRequestTotals } from "#modules/quoteRequests/domain/computeQuoteRequestTotals";
import { QuoteRequestDto } from "#modules/quoteRequests/infra/fetchQuoteRequest.api";
import { Icon } from "#shared/icons/Icon";
import { queryKeys } from "#shared/queryKeys/queryKeys";
import { ROUTES } from "#shared/routes";
import { Box } from "#shared/view/components/Box";
import { Flex } from "#shared/view/components/Flex";
import { Stack } from "#shared/view/components/Stack";
import {
  computeOptimizedMachineTimes,
  computeOptimizedMaterialCosts,
  formatPrice,
  ProcessStep,
} from "shared";

import classes from "./OptimizationsPanel.module.css";

type Props = {
  quoteRequest: QuoteRequestDto;
  quoteRequestLines: QuoteRequestLineDto[];
  quoteRequestProcessSteps: ProcessStep[];
  quoteRequestId: string;
  opened: boolean;
  onClose: () => void;
  optimizationIdToOpen?: string | null;
};

const SKELETON_CARD_COUNT = 4;
const SKELETON_THUMBNAIL_COUNT = 9;
const OPTIMIZATION_SETTINGS_HINT_KEY = "optimizationSettingsHint";

const isOptimizationSettingsHintDismissed = () => {
  try {
    return localStorage.getItem(OPTIMIZATION_SETTINGS_HINT_KEY) !== null;
  } catch {
    return true;
  }
};

const dismissOptimizationSettingsHint = () => {
  try {
    localStorage.setItem(OPTIMIZATION_SETTINGS_HINT_KEY, "true");
  } catch {
    return undefined;
  }
};

const OptimizationFiltersSkeleton = () => (
  <Flex align="center" justify="space-between" pr={spacing[4]}>
    <Flex gap={spacing[2]}>
      {[0, 1, 2].map((index) => (
        <Skeleton key={index} width={80} height={36} radius={radius.lg} />
      ))}
    </Flex>
    <Skeleton width={36} height={36} radius={radius.lg} />
  </Flex>
);

const OptimizationCardSkeleton = () => (
  <Box
    bg={colors.bg.elevation.primary}
    px={spacing[6]}
    pt={spacing[6]}
    pb={spacing[4]}
    style={{
      border: `1px solid ${colors.border.tertiary}`,
      borderRadius: radius.xl,
    }}
  >
    <Stack gap={spacing[4]}>
      <Flex align="flex-start" gap={spacing[4]} justify="space-between">
        <Stack gap={spacing[1]}>
          <Skeleton width={240} height={20} radius={radius.tiny} />
          <Skeleton width={160} height={14} radius={radius.tiny} />
        </Stack>
        <Skeleton width={32} height={32} radius={radius.md} />
      </Flex>
      <Flex gap={spacing[2]}>
        {Array.from({ length: SKELETON_THUMBNAIL_COUNT }, (_, index) => (
          <Skeleton key={index} width={48} height={48} radius={radius.xs} />
        ))}
      </Flex>
      <Divider type="secondary" />
      <Skeleton width={240} height={14} radius={radius.tiny} />
    </Stack>
  </Box>
);

const OptimizationsLoading = () => (
  <Stack gap={spacing[5]} data-testid="optimizations-loader">
    {Array.from({ length: SKELETON_CARD_COUNT }, (_, index) => (
      <OptimizationCardSkeleton key={index} />
    ))}
  </Stack>
);

const OptimizationsEmpty = ({
  hasCancelledOptimizations,
}: {
  hasCancelledOptimizations: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Stack align="center" gap={spacing[6]} px={spacing[12]} py={spacing[24]}>
      <Icon name="stars" size={32} color={colors.icon.secondary} />
      <Stack align="center" gap={spacing[2]}>
        <Title size="tiny">{t("optimizations.empty.title")}</Title>
        <Text size="base" color={colors.text.secondaryDefault} textAlign="center">
          {hasCancelledOptimizations
            ? t("optimizations.empty.cancelledDescription")
            : t("optimizations.empty.description")}
        </Text>
      </Stack>
    </Stack>
  );
};

export const OptimizationsPanel = ({
  quoteRequest,
  quoteRequestLines,
  quoteRequestProcessSteps,
  quoteRequestId,
  opened,
  onClose,
  optimizationIdToOpen = null,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<OptimizationFilter>("all");
  const [selectedOptimizationId, setSelectedOptimizationId] = useState<string | null>(null);
  const [isCancelledListOpen, setIsCancelledListOpen] = useState(false);
  const [isSettingsHintDismissed, setIsSettingsHintDismissed] = useState(
    isOptimizationSettingsHintDismissed,
  );
  const {
    optimizations = [],
    isLoading,
    error,
  } = useOptimizations({ quoteRequestId, enabled: opened });
  const { ignoreLine, isLineIgnored } = useIgnoredCompatibleLines(opened);
  const { toggleOptimization, isPending: isTogglePending } = useToggleOptimization({
    quoteRequestId,
  });

  const { companyMachines, isLoading: isCompanyMachinesLoading } = useCompanyMachines();

  const nestingCount = optimizations.filter((optimization) =>
    matchesOptimizationFilter(optimization, "nesting"),
  ).length;
  const processCount = optimizations.filter((optimization) =>
    matchesOptimizationFilter(optimization, "processes"),
  ).length;
  const allCount = optimizations.filter((optimization) =>
    matchesOptimizationFilter(optimization, "all"),
  ).length;
  const cancelledOptimizations = optimizations.filter((optimization) => optimization.deletedAt);
  const hasResult = !isLoading && !error;
  const isEmpty = hasResult && allCount === 0;

  const hideSettingsHint =
    isSettingsHintDismissed ||
    isCompanyMachinesLoading ||
    companyMachines.some((machine) => machine.canBeOptimized);

  const filters = [
    { value: "all" as const, label: t("optimizations.filters.all"), count: allCount },
    ...(nestingCount > 0
      ? [
          {
            value: "nesting" as const,
            label: t("optimizations.filters.nesting"),
            count: nestingCount,
          },
        ]
      : []),
    ...(processCount > 0
      ? [
          {
            value: "processes" as const,
            label: t("optimizations.filters.processes"),
            count: processCount,
          },
        ]
      : []),
  ];
  const activeFilter = filters.some((item) => item.value === filter) ? filter : "all";
  const visibleOptimizations = optimizations.filter((optimization) =>
    matchesOptimizationFilter(optimization, activeFilter),
  );
  useEffect(() => {
    if (opened && optimizationIdToOpen) {
      setSelectedOptimizationId(optimizationIdToOpen);
    }
  }, [opened, optimizationIdToOpen]);

  useEffect(() => {
    if (!opened) {
      setSelectedOptimizationId(null);
      setIsCancelledListOpen(false);
    }
  }, [opened]);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingExit, setPendingExit] = useState<"back" | "close" | null>(null);

  const leaveDetail = () => {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.optimizations.byQuoteRequest(quoteRequestId),
    });
    setSelectedOptimizationId(null);
    setHasUnsavedChanges(false);
  };
  const requestExit = (exit: "back" | "close") => {
    if (hasUnsavedChanges) {
      setPendingExit(exit);
      return;
    }
    if (exit === "back") {
      leaveDetail();
      return;
    }
    onClose();
  };
  const confirmExit = () => {
    leaveDetail();
    if (pendingExit === "close") {
      onClose();
    }
    setPendingExit(null);
  };

  const selectedOptimization = optimizations.find(
    (optimization) => optimization.id === selectedOptimizationId,
  );
  const dismissSettingsHint = () => {
    dismissOptimizationSettingsHint();
    setIsSettingsHintDismissed(true);
  };

  const toolbar = isLoading ? (
    <OptimizationFiltersSkeleton />
  ) : hasResult ? (
    <Flex align="center" justify="space-between" pr={spacing[4]}>
      <Flex gap={spacing[2]}>
        {filters.map((item) => (
          <Tab
            key={item.value}
            selected={activeFilter === item.value}
            count={item.count}
            onClick={() => setFilter(item.value)}
          >
            {item.label}
          </Tab>
        ))}
      </Flex>
      <div className={classes.settings}>
        <IconButton
          variant="secondary"
          size="md"
          aria-label={t("optimizations.settings")}
          onClick={() => {
            navigate(ROUTES.partnerSettingsMachines);
            dismissSettingsHint();
          }}
        >
          <Icon name="settings" size={16} />
        </IconButton>
        {hideSettingsHint ? null : (
          <div className={classes.hint}>
            <span className={classes.arrow} aria-hidden>
              <Icon name="hintArrow" size={16} color={colors.bg.elevation.tooltip} />
            </span>
            <Hint
              title={t("optimizations.hint.title")}
              description={t("optimizations.hint.description")}
              closeLabel={t("common.close")}
              onClose={dismissSettingsHint}
            />
          </div>
        )}
      </div>
    </Flex>
  ) : null;

  const appliedGain = sumAppliedOptimizationsGain(optimizations);
  const enabledOptimizations = optimizations.filter(
    (optimization) => optimization.enabled && !optimization.deletedAt,
  );
  const { totalPrice } = computeQuoteRequestTotals({
    quoteRequest,
    quoteRequestProcessSteps,
    quoteRequestLines,
    salesMarginRatio: quoteRequest.salesMarginRatio ?? 0,
    discountRatio: quoteRequest.discountRatio ?? 0,
    optimizedMachineTimes: computeOptimizedMachineTimes(enabledOptimizations),
    optimizedMaterialCosts: computeOptimizedMaterialCosts(enabledOptimizations),
  });

  const renderOptimizationCard = (optimization: (typeof optimizations)[number], key: string) => (
    <OptimizationCard
      key={key}
      optimization={optimization}
      onOpenDetail={() => {
        setIsCancelledListOpen(false);
        setSelectedOptimizationId(optimization.id);
      }}
      onToggle={(enabled) => toggleOptimization({ optimizationId: optimization.id, enabled })}
      isTogglePending={isTogglePending}
      isLineIgnored={(lineId) => isLineIgnored(optimization.id, lineId)}
      onIgnoreLine={(lineId) => ignoreLine(optimization.id, lineId)}
    />
  );

  const cancelledOptimizationsButton =
    hasResult && cancelledOptimizations.length > 0 ? (
      <Box
        component="button"
        type="button"
        onClick={() => setIsCancelledListOpen(true)}
        w="100%"
        bg={colors.bg.elevation.tertiary}
        px={spacing[8]}
        py={spacing[4]}
        style={{
          border: "none",
          cursor: "pointer",
          borderTopLeftRadius: radius.xl,
          borderTopRightRadius: radius.xl,
          textAlign: "left",
        }}
      >
        <Flex align="center" justify="space-between" w="100%">
          <Text size="small">
            {t("optimizations.cancelled.count", { count: cancelledOptimizations.length })}
          </Text>
          <Icon name="chevronRight" size={16} color={colors.icon.primaryDefault} />
        </Flex>
      </Box>
    ) : null;

  const footer = hasResult ? (
    <Flex align="center" justify="space-between" w="100%" gap={spacing[4]}>
      <Flex gap={spacing[4]}>
        <Stack gap={0} w={138}>
          <Text size="small" color={colors.text.secondaryDefault}>
            {t("optimizations.footer.appliedGain")}
          </Text>
          <Text size="small" type="medium" color={colors.text.success}>
            {formatGain(appliedGain)}
          </Text>
        </Stack>
        <Stack gap={0} w={138}>
          <Text size="small" color={colors.text.secondaryDefault}>
            {t("optimizations.footer.totalPrice")}
          </Text>
          <Text size="small" type="medium">
            {formatPrice(totalPrice)}
          </Text>
        </Stack>
      </Flex>
      <Button variant="primary" size="lg" onClick={onClose}>
        {t("optimizations.done")}
      </Button>
    </Flex>
  ) : isLoading ? (
    <Flex justify="flex-end" w="100%">
      <ButtonSkeleton size="lg" width={95} />
    </Flex>
  ) : null;

  return (
    <SidePanel opened={opened} onClose={() => requestExit("close")}>
      {selectedOptimization ? (
        <OptimizationDetail
          optimization={selectedOptimization}
          onBack={leaveDetail}
          onRequestBack={() => requestExit("back")}
          onUnsavedChangesChange={setHasUnsavedChanges}
        />
      ) : isCancelledListOpen ? (
        <>
          <SidePanelHeader
            title={t("optimizations.cancelled.title")}
            onBack={() => setIsCancelledListOpen(false)}
            backText={t("optimizations.detail.back")}
            onClose={onClose}
            closeText={t("common.close")}
          />
          <SidePanelContent>
            <Stack gap={spacing[5]}>
              {cancelledOptimizations.map((optimization) =>
                renderOptimizationCard(optimization, optimization.id),
              )}
            </Stack>
          </SidePanelContent>
        </>
      ) : (
        <>
          <SidePanelHeader
            title={t("optimizations.title")}
            onClose={onClose}
            closeText={t("common.close")}
          >
            {toolbar}
          </SidePanelHeader>
          <SidePanelContent>
            {isLoading ? (
              <OptimizationsLoading />
            ) : error ? (
              <Text size="small" color={colors.text.errorDefault}>
                {error.message}
              </Text>
            ) : isEmpty ? (
              <OptimizationsEmpty hasCancelledOptimizations={cancelledOptimizations.length > 0} />
            ) : (
              <Stack gap={spacing[5]}>
                {visibleOptimizations.map((optimization) =>
                  renderOptimizationCard(optimization, optimization.id),
                )}
              </Stack>
            )}
          </SidePanelContent>
          {cancelledOptimizationsButton}
          <SidePanelFooter
            withFooterBorder={footer !== null && cancelledOptimizationsButton === null}
          >
            {footer}
          </SidePanelFooter>
        </>
      )}
      <DiscardChangesModal
        opened={pendingExit !== null}
        onLeave={confirmExit}
        onKeepEditing={() => setPendingExit(null)}
      />
    </SidePanel>
  );
};
